import { CurrencyPipe } from '@angular/common';
import { Component } from '@angular/core';

import { Store, createSelector } from '@ngrx/store';
import { MenuItem } from 'primeng/api';

import { isNavigatorStarted, resolveNavigatorUrl, validateLocationsForEventType } from 'src/app/job/job-helpers';

import { createConfigSelector, selectEventTypes, selectNavigatorUrlsByEventType } from 'src/app/state/branding.store';
import { selectFeature } from 'src/app/state/featureFlags.store';

import { environment } from '../../../../environments/environment';
import { Job_CalendarEventFragment } from '../../../../generated/graphql.generated';
import { FreyaCommonModule } from '../../../freya-common/freya-common.module';
import { convertCentsToDollars } from '../../../lib.ts/currency.util';
import { AvailabilityScheduleComponent } from '../../../shared/availability-schedule/availability-schedule.component';
import { JobToolActions } from '../../job-tool.actions';
import { formatEventDetails, selectJob, selectJobLoaded } from '../../job-tool.reducer';
import { WorkOrdersActions } from '../../job-workorders/job-workorders.actions';
import { selectCanRunNavigator } from '../../job-workorders/job-workorders.selectors';



import { OverviewTimelineActions } from './overview-timeline.actions';
import { overviewTimelineFeature } from './overview-timeline.feature';

export type EventsWithMissingLocations = Record<string, string[]>;

@Component({
  selector: 'app-overview-timeline',
  standalone: true,
  imports: [
    FreyaCommonModule,
    AvailabilityScheduleComponent,
  ],
  templateUrl: './overview-timeline.component.html',
  styleUrl: './overview-timeline.component.scss'
})
export class OverviewTimelineComponent {

  public viewModel$ = this.store.select(createSelector({
    jobLoaded: selectJobLoaded,
    // Hides the timeline's navigator button while the navigator is unreleased.
    // Visibility only — see the note in job-workorders.component.ts.
    crewNavigatorEnabled: selectFeature('crew-navigator'),
    // Crew Lead and above only — see selectCanRunNavigator.
    canRunNavigator: selectCanRunNavigator,
    events: createSelector(
      selectJob,
      createConfigSelector('NAVIGATOR_URL'),
      selectNavigatorUrlsByEventType,
      selectEventTypes,
      (job, configDefaultNavigatorUrl, navigatorUrlsByEventType, eventTypes) => job?.events.map((event) => {

        const validation = validateLocationsForEventType(job.locations || [], event.type);
        const missingLocations = [...validation.missingLocations, ...validation.invalidLocations];
        const isMissingLocations = !validation.isValid;

        const canBeInvoiced = !(event.invoices?.some((i) => !i.deletedAt && !i.voidedAt));

        const showPromoteEventButton = [ 'booked', 'confirmed', 'completed' ].includes(event.status) && canBeInvoiced;

        const { style, title, datetime, assetDetails, totalCrew } = formatEventDetails(event, job.zone?.timezone);

        const hasNavigatorUrl = Boolean(resolveNavigatorUrl(event, {
          jobNavigatorUrl: job?.metadata?.navigatorUrl,
          typeUrls: navigatorUrlsByEventType,
          eventTypes,
          defaultUrl: configDefaultNavigatorUrl,
        }));

        const discountedSubTotal = this.currency.transform(
          convertCentsToDollars(event.discountedSubTotal),
          job.currency || environment.defaultCurrency,
        );

        const secondLineDetails = [
          discountedSubTotal,
          assetDetails,
          totalCrew,
        ].filter(Boolean).join(' | ');

        return {
          style,
          event,
          title,
          datetime,
          secondLineDetails,
          showPromoteEventButton,
          missingLocations,
          isMissingLocations,
          hasNavigatorUrl,
          navigatorStarted: isNavigatorStarted(event),
          missingLocationsTooltip: isMissingLocations ? validation.errorMessage : undefined,
        }
      }) || [],
    ),
    currency: createSelector(selectJob, (job) => job?.currency),
    eventMenuItems: createSelector(
      selectJob,
      overviewTimelineFeature.selectTimelineActionsEventId,
      (job, eventId) => this._createMenuItems(job?.events?.find((e) => e.id === eventId), job?.id),
    ),
  }));

  public actions = OverviewTimelineActions;

  /**
   * The navigator launch lives in the workorders feature, but its effect resolves everything
   * from `selectJob` / `selectJobId`, so it works from any tab.
   *
   * This timeline used to dispatch its own `OverviewTimelineActions.startEventButtonClicked`,
   * whose effect built the node tree and pushed it into the store but never wrote the
   * mutation or navigated — a silent no-op that still logged a successful 17-node tree to the
   * console, which made it look like it had worked. That effect and action are now removed.
   */
  public workOrdersActions = WorkOrdersActions;

  constructor(
    public store: Store,
    private currency: CurrencyPipe,
  ) { }

  private _createMenuItems(event: Job_CalendarEventFragment, jobId: string): MenuItem[] {

    if (!event) { return []; }

    return [
      {
        label: 'Print BOL',
        icon: 'pi pi-print',
        command: () => {
            if (jobId) {
                this.store.dispatch(JobToolActions.printBolRequested({ eventId: event.id, jobId }));
            }
        },
      },
      {
        label: 'Confirm',
        icon: 'pi pi-check',
        command: () => this.store.dispatch(this.actions.confirmEventMenuItemSelected()),
        visible: event.status === 'booked',
      },
      {
        id: 'complete',
        label: 'Mark complete',
        icon: 'pi pi-check-circle',
        command: () => this.store.dispatch(this.actions.completeEventMenuItemSelected()),
        visible: [ 'booked', 'confirmed' ].includes(event.status),
      },
      {
        label: 'Book',
        icon: 'pi pi-calendar-plus',
        command: () => this.store.dispatch(this.actions.bookEventMenuItemSelected()),
        visible: !event.start,
      },
      {
        label: 'Reschedule',
        icon: 'pi pi-calendar-plus',
        command: () => this.store.dispatch(this.actions.rescheduleEventMenuItemSelected()),
        visible: Boolean(event.start),
      },
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.store.dispatch(this.actions.editEventMenuItemSelected()),
      },
      {
        label: 'More Info',
        icon: 'pi pi-info-circle',
        command: () => this.store.dispatch(this.actions.moreInfoMenuItemSelected()),
      },
      {
        label: 'Cancel',
        icon: 'pi pi-times',
        command: () => this.store.dispatch(this.actions.cancelEventMenuItemSelected()),
        visible: [ 'pending', 'booked', 'confirmed', 'completed' ].includes(event.status),
      },
      {
        label: 'Undo Cancel',
        icon: 'pi pi-undo',
        command: () => this.store.dispatch(this.actions.undoCancelMenuItemSelected()),
        visible: event.status === 'cancelled',
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        command: () => this.store.dispatch(this.actions.deleteMenuItemSelected()),
        visible: [ 'required', 'cancelled' ].includes(event.status),
      },
    ];
  }

}
